import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Sparkles, Share2, LogIn, ShieldCheck, Trophy, Rocket } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  cadet:     "border-slate-400/60  text-slate-200",
  recruit:   "border-cyan-400/70   text-cyan-300",
  pilot:     "border-emerald-400/70 text-emerald-300",
  captain:   "border-purple-400/70  text-purple-300",
  commander: "border-amber-300/80   text-amber-200",
};

export default function AlienId() {
  const { t } = useTranslation();
  const { user, login, loading: authLoading } = useAuth();
  const [handle, setHandle] = useState("");
  const [profile, setProfile] = useState(null);
  const [pilotStats, setPilotStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const stableProfile = useMemo(() => profile, [profile]);

  const fetchMine = useCallback(async () => {
    if (!user) { setProfile(null); setPilotStats(null); return; }
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        fetch(`${API}/api/passport/me`, { credentials: "include" }),
        fetch(`${API}/api/pilot/me/stats`, { credentials: "include" }),
      ]);
      if (pRes.ok) {
        const d = await pRes.json();
        if (d.passport) setProfile(d.passport);
      }
      if (sRes.ok) setPilotStats(await sRes.json());
    } catch { /* silent */ }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchMine(); }, [fetchMine]);

  const onGenerate = async (e) => {
    e?.preventDefault();
    if (!user) { toast.error(t("alien_id.toast_login_first")); return; }
    if (!handle.trim()) { toast.error(t("alien_id.toast_need_handle")); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/passport/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ handle }),
      });
      const d = await r.json();
      if (!r.ok) {
        if (r.status === 409 && d?.detail?.passport) {
          setProfile(d.detail.passport);
          toast.info(t("alien_id.toast_already_have"));
        } else {
          toast.error(d?.detail?.message || d?.detail || t("alien_id.toast_generic_error"));
        }
      } else {
        setProfile(d.passport);
        toast.success(t("alien_id.toast_register_success"));
      }
    } catch {
      toast.error(t("alien_id.toast_network_error"));
    }
    setCreating(false);
  };

  const onShare = () => {
    if (!stableProfile) return;
    const trophy = pilotStats && pilotStats.best_score > 0
      ? t("alien_id.share_trophy_line", { label: pilotStats.tier.label, score: pilotStats.best_score.toLocaleString() })
      : "";
    const text = t("alien_id.share_text", {
      rank: stableProfile.rank, handle: stableProfile.handle,
      planet: stableProfile.planet, code: stableProfile.code, trophy,
    });
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
  };

  const onCopy = async () => {
    if (!stableProfile) return;
    const txt = `${stableProfile.rank} ${stableProfile.handle} · ${stableProfile.code} · ${stableProfile.planet}`;
    try { await navigator.clipboard.writeText(txt); toast.success(t("alien_id.toast_copy_success")); }
    catch { toast.error(t("alien_id.toast_copy_fail")); }
  };

  const hasPassport = !!stableProfile;

  return (
    <section id="alien-id" data-testid="alien-id-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="max-w-2xl">
          <p className="font-mono-alt text-[10px] tracking-[0.3em] text-secondary">{t("alien_id.section_label")}</p>
          <h2 className="mt-3 font-display font-black tracking-tighter text-5xl md:text-6xl leading-[0.95]">
            {t("alien_id.title_l1")}
            <br />
            <span className="text-primary neon-glow-primary">{t("alien_id.title_l2")}</span>
            <br />
            {t("alien_id.title_l3")}
          </h2>
          <p className="mt-6 text-muted-foreground">{t("alien_id.intro")}</p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8 items-start">
          <motion.form
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            onSubmit={onGenerate}
            className="lg:col-span-5 glass rounded-sm border border-primary/25 p-6 md:p-8 space-y-5"
          >
            {!user ? (
              <>
                <div className="flex items-start gap-3 rounded-sm border border-primary/30 bg-primary/5 p-4">
                  <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display font-bold text-lg">{t("alien_id.login_required_title")}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{t("alien_id.login_required_desc")}</p>
                  </div>
                </div>
                <Button type="button" onClick={login} disabled={authLoading} data-testid="alien-id-login-btn"
                  className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono-alt tracking-widest text-xs">
                  <LogIn className="mr-2 h-4 w-4" /> {t("alien_id.cta_login")}
                </Button>
              </>
            ) : (
              <>
                <label className="block font-mono-alt text-[10px] tracking-widest text-muted-foreground">
                  {hasPassport ? t("alien_id.handle_label_registered") : t("alien_id.handle_label_new")}
                </label>
                <input
                  value={hasPassport ? stableProfile.handle : handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder="@seuNome"
                  maxLength={20}
                  disabled={hasPassport || loading}
                  data-testid="alien-id-input"
                  className="w-full rounded-sm border border-primary/30 bg-black/60 px-4 py-3 text-base font-mono-alt text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/40 disabled:opacity-70"
                />
                {!hasPassport ? (
                  <Button type="submit" disabled={creating || loading} data-testid="alien-id-generate-btn"
                    className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono-alt tracking-widest text-xs">
                    <Sparkles className="mr-2 h-4 w-4" />
                    {creating ? t("alien_id.cta_generating") : t("alien_id.cta_generate")}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 rounded-sm border border-primary/40 bg-primary/10 px-3 py-2">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    <span className="font-mono-alt text-[10px] tracking-widest text-primary">
                      {t("alien_id.registered_badge")}
                    </span>
                  </div>
                )}
                <p className="font-mono-alt text-[10px] tracking-widest text-muted-foreground pt-2 border-t border-primary/15">
                  {t("alien_id.one_per_email")}
                </p>
              </>
            )}
          </motion.form>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="lg:col-span-7" data-testid="alien-id-card-wrapper">
            {!stableProfile ? (
              <div className="glass rounded-sm border border-dashed border-primary/25 p-10 md:p-14 text-center min-h-[360px] flex flex-col items-center justify-center">
                <div className="h-16 w-16 rounded-full border border-primary/30 flex items-center justify-center mb-6 animate-pulse">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <p className="font-display font-bold text-xl">{t("alien_id.waiting_title")}</p>
                <p className="mt-2 text-muted-foreground max-w-sm">
                  {user ? t("alien_id.waiting_desc_logged_in") : t("alien_id.waiting_desc_logged_out")}
                </p>
              </div>
            ) : (
              <motion.div key={stableProfile.code} initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 120, damping: 15 }}
                className="glass rounded-sm border-2 border-primary/60 p-6 md:p-8 relative overflow-hidden" data-testid="alien-id-card">
                <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="font-mono-alt text-[10px] tracking-widest text-secondary">{t("alien_id.card_year_label", { year: stableProfile.year })}</p>
                    <h3 className="mt-1 font-display font-black text-3xl md:text-4xl tracking-tighter text-primary neon-glow-primary">
                      {stableProfile.rank}
                    </h3>
                    <p className="mt-1 font-mono-alt text-lg text-foreground">{stableProfile.handle}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono-alt text-[9px] tracking-widest text-muted-foreground">{t("alien_id.card_code_label")}</p>
                    <p className="font-mono-alt text-xl text-primary">{stableProfile.code}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 border-t border-primary/20 pt-4">
                  <div>
                    <p className="font-mono-alt text-[9px] tracking-widest text-muted-foreground">{t("alien_id.card_origin_label")}</p>
                    <p className="mt-1 font-display font-bold text-foreground">{stableProfile.planet}</p>
                  </div>
                  <div>
                    <p className="font-mono-alt text-[9px] tracking-widest text-muted-foreground">{t("alien_id.card_mission_label")}</p>
                    <p className="mt-1 text-sm text-foreground">{stableProfile.mission}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="font-mono-alt text-[9px] tracking-widest text-muted-foreground">{t("alien_id.card_traits_label")}</p>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {stableProfile.traits?.map((tr) => (
                        <span key={tr} className="rounded-sm border border-primary/30 bg-black/40 px-2 py-1 font-mono-alt text-xs">{tr}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-4 border-t border-primary/20 pt-4" data-testid="alien-id-trophy-row">
                  {pilotStats && pilotStats.best_score > 0 ? (
                    <div className={`rounded-sm border ${TIER_COLORS[pilotStats.tier?.tier] || "border-primary/30 text-primary"} bg-black/50 p-3 flex items-center justify-between gap-3`}>
                      <div className="flex items-center gap-3">
                        <Trophy className="h-5 w-5 shrink-0" />
                        <div>
                          <p className="font-mono-alt text-[9px] tracking-widest text-muted-foreground">{t("alien_id.trophy_label")}</p>
                          <p className="font-display font-bold text-lg tracking-tight">
                            {pilotStats.tier?.label} · {pilotStats.best_score.toLocaleString()} pts
                          </p>
                          <p className="font-mono-alt text-[9px] tracking-widest text-muted-foreground">
                            {t(pilotStats.total_runs === 1 ? "alien_id.trophy_max_level" : "alien_id.trophy_max_level_plural", { level: pilotStats.best_level, runs: pilotStats.total_runs })}
                          </p>
                        </div>
                      </div>
                      {pilotStats.tier?.percent_off > 0 && (
                        <span className="hidden sm:inline-flex font-mono-alt text-[9px] tracking-widest px-2 py-1 rounded-sm border border-current">
                          {t("alien_id.trophy_off_shop", { percent: pilotStats.tier.percent_off })}
                        </span>
                      )}
                    </div>
                  ) : (
                    <a href="/pilot" data-testid="alien-id-trophy-cta"
                      className="rounded-sm border border-dashed border-primary/40 bg-black/40 p-3 flex items-center justify-between gap-3 hover:border-primary hover:bg-primary/5 transition-colors">
                      <div className="flex items-center gap-3">
                        <Rocket className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-mono-alt text-[9px] tracking-widest text-secondary">{t("alien_id.trophy_cta_label")}</p>
                          <p className="font-display font-bold text-base tracking-tight text-white">{t("alien_id.trophy_cta_desc")}</p>
                        </div>
                      </div>
                      <span className="hidden sm:inline-flex font-mono-alt text-[9px] tracking-widest text-primary">{t("alien_id.trophy_cta_play")}</span>
                    </a>
                  )}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button type="button" onClick={onShare} data-testid="alien-id-share-btn"
                    className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono-alt tracking-widest text-xs">
                    <Share2 className="mr-2 h-4 w-4" /> {t("alien_id.cta_share")}
                  </Button>
                  <Button type="button" onClick={onCopy} variant="outline" data-testid="alien-id-copy-btn"
                    className="rounded-sm border-primary/40 text-primary hover:bg-primary/10 hover:text-primary font-mono-alt tracking-widest text-xs">
                    {t("alien_id.cta_copy")}
                  </Button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
